import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosVisibilityService } from '@/services/RorosService';
import { rorosKeys } from './keys';
import type { SetVisibilityOverridePayload } from '@/types/roros/actors';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosVisibility(entityType: string, entityId: string) {
  return useQuery({
    queryKey: rorosKeys.visibility.entity(entityType, entityId),
    queryFn: () => RorosVisibilityService.get(entityType, entityId),
    enabled: !!entityType && !!entityId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosVisibilitySet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      payload,
    }: {
      entityType: string;
      entityId: string;
      payload: SetVisibilityOverridePayload;
    }) => RorosVisibilityService.set(entityType, entityId, payload),
    onSuccess: (_data, { entityType, entityId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.visibility.entity(entityType, entityId) });
    },
  });
}

export function useRorosVisibilityRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      RorosVisibilityService.remove(entityType, entityId),
    onSuccess: (_data, { entityType, entityId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.visibility.entity(entityType, entityId) });
    },
  });
}
